/* eslint-disable consistent-return */
import { createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { toast } from "utils/toast";
import i18next from "i18next";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import DealerCare from "services/dealerCareApi";

import {
  DealerUserCreateRequest,
  DealerUserCreateResponse,
  DealerUserDeleteRequest,
  DealerUserEditRequest,
  DealerUserEditResponse,
  DealerUserRecoveryPasswordRequest,
  DealerUserRecoveryPasswordResponse,
  DealerUserSendWelcomeMailRequest,
  DealerUserSendWelcomeMailResponse,
  DealerUsersSearchRequest,
  DealerUsersSearchResponse,
} from "./types.store";

export const handleGetDealerUsersRequest = createAsyncThunk<
  DealerUsersSearchResponse,
  DealerUsersSearchRequest
>("getSearchDealerUsers", async (params, { rejectWithValue, dispatch }) => {
  const showGlobalSpinner = params.suppressGlobalSpinner !== true;
  try {
    if (showGlobalSpinner) {
      dispatch(setIsSpinnerVisible(true));
    }

    const isProfile = params.profile ? `&profile=${params.profile}` : "";
    const isStatus = params.statusOn ? `&status=${params.statusOn}` : "";

    const offsetLimit = params.offset * params.limit;

    const { data } = await DealerCare.get(
      `/v1/dealers/${params.dealerId}/users?limit=${params.limit}&offset=${offsetLimit}${isStatus}${isProfile}`,
    );

    return data;
  } catch (err) {
    toast(i18next.t("failedLoadUsersPleaseAgain").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    if (showGlobalSpinner) {
      dispatch(setIsSpinnerVisible(false));
    }
  }
});

export const sendEmailWelcomeRequest = createAsyncThunk<
  DealerUserSendWelcomeMailResponse,
  DealerUserSendWelcomeMailRequest
>("postSendWelcomeByMail", async (body, { rejectWithValue }) => {
  try {
    await DealerCare.post(`/v1/dealers/SendWelcomeByMail`, body);
  } catch (err) {
    toast(i18next.t("failedToSendMail").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  }
});

export const sendEmailRecoverPasswordRequest = createAsyncThunk<
  DealerUserRecoveryPasswordResponse,
  DealerUserRecoveryPasswordRequest
>("postRecoverPasswordByMail", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.post(`/v1/dealers/RecoverPasswordByMail`, body);
  } catch (err) {
    toast(i18next.t("failedToSendMail").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleCreateDealerUserRequest = createAsyncThunk<
  DealerUserCreateResponse,
  DealerUserCreateRequest
>("postCreateDealerUser", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const {
      data: {
        data: { id },
      },
    } = await DealerCare.post<{ data: { id: string } }>(
      `/v1/dealers/${body.dealerId}/users`,
      body,
    );

    await dispatch(
      sendEmailWelcomeRequest({
        delearId: body.dealerId,
        userId: id,
        email: body.email,
        name: body.name,
      }),
    ).unwrap();

    await dispatch(
      handleGetDealerUsersRequest({
        dealerId: body.dealerId,
        limit: 20,
        offset: 0,
      }),
    );
  } catch (err) {
    toast(i18next.t("failedCreateUserPleaseAgain").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleEditDealerUserRequest = createAsyncThunk<
  DealerUserEditResponse,
  DealerUserEditRequest
>("putEditDealerUser", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.put(
      `/v1/dealers/${body.dealerId}/users/${body.userId}`,
      body,
    );

    await dispatch(
      handleGetDealerUsersRequest({
        dealerId: body.dealerId,
        limit: 20,
        offset: 0,
      }),
    );
  } catch (err) {
    toast(i18next.t("failedEditUserPleaseAgain").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleDeleteDealerUserRequest = createAsyncThunk<
  void,
  DealerUserDeleteRequest
>("deleteDeleteDealerUser", async (body, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await DealerCare.delete(
      `/v1/dealers/${body.dealerId}/users/${body.userId}`,
    );

    await dispatch(
      handleGetDealerUsersRequest({
        dealerId: body.dealerId,
        limit: 20,
        offset: 0,
      }),
    );
  } catch (err: any) {
    if (body.profile === "deliveryman") {
      return rejectWithValue(err.response.data);
    }
    toast(i18next.t("failedDeleteUserPleaseAgain").toString(), {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});
